import { addDoc, onSnapshot } from 'firebase/firestore'
import type { DocumentReference, Timestamp, Unsubscribe } from 'firebase/firestore'
import { defineStore } from 'pinia'
import type { Ref } from 'vue'
import { ref } from 'vue'
import { ChangeLogModel, mapDataChangeLog } from '~/models/ChangeLogModel'
import store from '../store'
import { collectionChangesLogs, getCollectionChangesReport } from '~/firestoreWrappers/firestoreWrappers'
import { compareRouteGetProducts, getDifferences } from '~/helpers/differences'

export const useChangeLogsStore = defineStore('changeLogs', () => {
  const logs: Ref<ChangeLogModel[] | null> = ref(null)
  const unsubscribe: Ref<Unsubscribe | null> = ref(null)

  function resetState() {
    logs.value = null
    if (unsubscribe.value) {
      unsubscribe.value()
      unsubscribe.value = null
    }
  }

  function getLogs(company: DocumentReference, startDate: Timestamp, endDate: Timestamp) {
    store.dispatch('shared/init')

    if (unsubscribe.value) {
      unsubscribe.value()
    }

    const onSuccess = (snapshot: any) => {
      logs.value = snapshot.docs.map(mapDataChangeLog)
      store.dispatch('shared/success')
    }

    const onError = (error: any) => {
      console.error(error)
      store.dispatch('shared/error', error)
    }

    unsubscribe.value = onSnapshot(
      getCollectionChangesReport(company, startDate, endDate),
      onSuccess,
      onError,
    )
  }

  function addLog<T>(
    obj1: T,
    obj2: T,
    userRef: DocumentReference,
    userName: string,
    name: string,
    collectionChanged: DocumentReference,
    city: string,
    route: DocumentReference | null,
    documentDate: Timestamp | null,
    company: DocumentReference,
    isDeliversSells: boolean = false,
  ) {
    const differences = isDeliversSells ? [...getDifferences(obj1, obj2), ...compareRouteGetProducts(obj1, obj2)] : getDifferences(obj1, obj2)

    const newLog = new ChangeLogModel({
      date: null,
      documentDate,
      userChanged: userRef,
      userName,
      collectionChanged,
      changes: differences,
      city,
      name,
      route,
      company,
    })

    const onSuccess = () => {
      logs.value = [...(logs.value || []), newLog]
      store.dispatch('shared/success')
    }

    const onError = (error: any) => {
      console.error(error)
      store.dispatch(
        'shared/error',
        error,
      )
    }

    addDoc(collectionChangesLogs, newLog.toMap())
      .then(onSuccess)
      .catch(onError)
  }

  function addNewDocumentLog(
    userRef: DocumentReference,
    userName: string,
    name: string,
    collectionChanged: DocumentReference,
    city: string,
    route: DocumentReference | null,
    documentDate: Timestamp | null,
    company: DocumentReference,
  ) {
    const newLog = new ChangeLogModel({
      date: null,
      documentDate,
      userChanged: userRef,
      userName,
      collectionChanged,
      changes: [],
      city,
      name,
      route,
      company,
      isNewDocument: true,
    })

    const onSuccess = () => {
      logs.value = [...(logs.value || []), newLog]
      store.dispatch('shared/success')
    }

    const onError = (error: any) => {
      console.error(error)
      store.dispatch(
        'shared/error',
        error,
      )
    }

    addDoc(collectionChangesLogs, newLog.toMap())
      .then(onSuccess)
      .catch(onError)
  }

  return {
    logs,
    resetState,
    getLogs,
    addLog,
    addNewDocumentLog,
  }
})
